import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useToast } from '../../Helpers/ToastHelper';
import { useTranslation } from 'react-i18next';
import { getSettings } from '../../Helpers/SettingsHelper';
import { Paths } from '../../Common/Paths';
import { classNames } from 'primereact/utils';
import { FloatLabel, Card, InputText, Button, ProgressBar, Dialog, SCIcon } from "../../Common/Imports"
import AuthService from '../../Helpers/Authentication/AuthService';
import { useAuth } from '../../Helpers/Authentication/AuthContext';

interface FormData {
    Email: string;
    Password: string;
}

export const Login: React.FC = () => {
    const { t } = useTranslation();
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [searchParams] = useSearchParams();
    const email = searchParams.get("email");
    const navigate = useNavigate();
    const { register, handleSubmit, formState: { errors } } = useForm<FormData>();
    const config = getSettings();
    const { showToast } = useToast();
    const { setSessionToken, setSessionUser } = useAuth();
    const [ModalVisible, setModalVisible] = useState<boolean>(false);
    const [ModalContent, setModalContent] = useState<string>("");

    useEffect(() => {
        setModalVisible(config.loginBanner.showLoginBanner);
        setModalContent(config.loginBanner.loginBannerURL);
    }, [config.loginBanner.loginBannerURL, config.loginBanner.showLoginBanner]);

    const onSubmit = async (data: FormData) => {
        setShowLoader(true);
        try {
            const result = await AuthService.login(data.Email, data.Password);
            if (result) {
                setSessionToken(result.data.accessToken);
                setSessionUser(result.data.userInfos);
            }
            navigate(Paths['Home'].value);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                showToast('error', 'Error', error.response?.data.message || t("Auth.Login.Fail"));
            } else {
                showToast('error', 'Error', t("Auth.Login.Error"));
            }
        }
        finally {
            setShowLoader(false);
        }
    };


    function formFooter() {
        return (
            !showLoader &&
            <>
                <Button type="submit" label={t("Auth.Login.Button")} className="w-100" />
                <div className="flex align-items-center justify-content-center mt-4 gap-3">
                    <Button label={t("Auth.Login.Link")} className="p-button-text" onClick={() => navigate(Paths['ResetPasswordRequest'].value)} />
                    <Button label={t("Auth.Login.Register")} className="p-button-text" onClick={() => navigate(Paths['Register'].value)} />
                </div>
            </>
        );
    }

    function formHeader() {
        return (
            <>
                <img src={`${config.settings.cdnUrl}${Paths.Icon.value}`} alt="logo" className="w-5rem" />
                <h1 className="text-4xl font-light mt-4 text-primary-500">
                    {t("Auth.Login.Header")}
                </h1>
                <p>
                    {
                        showLoader ?
                            t("Auth.Login.LoggingIn")
                            :
                            t("Auth.Login.SubHeader")
                    }
                </p>
            </>
        );
    }

    return (
        <>
            <Dialog
                visible={ModalVisible}
                onHide={() => { if (!ModalVisible) return; setModalVisible(false); }}
                content={() => (
                    <div style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                    }}>
                        <SCIcon
                            Name="Close"
                            OnClick={() => setModalVisible(false)}
                            style={{
                                position: 'absolute',
                                top: '10px',
                                right: '15px',
                                fontSize: '18px',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                zIndex: 10, // Ensure it is above the image
                            }}
                        />
                        <img
                            src={ModalContent}
                            alt="Modal Content"
                            style={{
                                maxWidth: '100%', // Scale down if too wide
                                maxHeight: '100%', // Scale down if too tall
                            }}
                        />
                    </div>
                )}
            >
                
            </Dialog>

            <div className="z-5 w-full px-6 text-center mt-8" style={{ maxWidth: '500px' }}>
                <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
                    <Card footer={formFooter} header={formHeader}>
                        {showLoader ? (
                            <>
                                <ProgressBar mode="indeterminate" style={{ height: '6px' }} />
                            </>
                        ) : (
                            <>

                                <div className="row mb-3">
                                    <div className="col-md-12 mb-4">
                                        <FloatLabel>
                                            <label htmlFor="UserName">
                                                {t("Auth.Login.Form.User.Username")}
                                            </label>
                                            <InputText
                                                value={email}
                                                id="UserName"
                                                {
                                                ...register(
                                                    'Email',
                                                    {
                                                        required: t("Auth.Login.Form.User.Username") + " " + t("Auth.Login.Form.Required")
                                                    })
                                                }
                                                className={
                                                    classNames({ 'p-invalid': errors.Email })
                                                } />
                                        </FloatLabel>
                                        {
                                            errors.Email &&
                                            <small className="p-error">
                                                {errors.Email.message}
                                            </small>
                                        }
                                    </div>
                                </div>
                                <div className="row mb-3">
                                    <div className="col-md-12">
                                        <FloatLabel>
                                            <label htmlFor="Password">
                                                {t("Auth.Login.Form.User.Password")}
                                            </label>
                                            <InputText
                                                id="Password"
                                                type="password"
                                                {
                                                ...register(
                                                    'Password',
                                                    {
                                                        required: t("Auth.Login.Form.User.Password") + " " + t("Auth.Login.Form.Required")
                                                    })
                                                }
                                                className={
                                                    classNames({ 'p-invalid': errors.Password })
                                                } />
                                        </FloatLabel>
                                        {
                                            errors.Password &&
                                            <small className="p-error">
                                                {errors.Password.message}
                                            </small>
                                        }
                                    </div>
                                </div>
                            </>
                        )}

                    </Card>
                </form>
            </div >
        </>
    );
};

export default Login;